import { z } from "zod";
import { DOCX_MIME_TYPE } from "../constants/fileTypes";

/* ==================================================================
 * Shared helpers
 * ================================================================== */

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* ==================================================================
 * Author schema
 * ================================================================== */

const authorSchema = z
  .object({
    first_name: z
      .string()
      .min(1, "First name is required")
      .max(100, "First name must be less than 100 characters"),
    last_name: z
      .string()
      .min(1, "Last name is required")
      .max(100, "Last name must be less than 100 characters"),
    corresponding_email: z.string().optional(),
    is_corresponding: z.boolean(),
    position: z.string().optional(),
    abstract_author_institutions_attributes: z
      .array(
        z.object({
          abstract_institution_id: z.string().min(1, "Institution is required"),
        }),
      )
      .min(1, "At least one institution is required"),
  })
  .superRefine((author, ctx) => {
    // If author is marked as corresponding, email is required and must be valid
    if (author.is_corresponding) {
      if (
        !author.corresponding_email ||
        author.corresponding_email.trim() === ""
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Email is required for corresponding author",
          path: ["corresponding_email"],
        });
      } else if (!emailRegex.test(author.corresponding_email.trim())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please enter a valid email address",
          path: ["corresponding_email"],
        });
      }
    }
  });

/* ==================================================================
 * Institution
 * ================================================================== */

const institutionSchema = z
  .string()
  .min(1, "Institution name is required")
  .max(200, "Institution name must be less than 200 characters");

/* ==================================================================
 * Poster Award
 * ================================================================== */

const posterAwardSchema = z
  .object({
    paParticipation: z.boolean(),
    paAge: z.coerce.number().optional(),
    paProofOfIdentity: z.any().optional(),
  })
  .superRefine((data, ctx) => {
    // If paParticipation is true, require paAge and paProofOfIdentity
    if (data.paParticipation) {
      // Validate paAge
      if (!data.paAge) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Age is required for Poster Award participation",
          path: ["paAge"],
        });
      } else {
        // Validate minimum age
        const age = parseInt(data.paAge.toString(), 10);
        if (isNaN(age) || age < 18) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "You must be at least 18 years old for Poster Award participation",
            path: ["paAge"],
          });
        }
      }

      // Validate paProofOfIdentity
      if (!data.paProofOfIdentity) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Proof of identity is required for Poster Award participation",
          path: ["paProofOfIdentity"],
        });
      } else {
        // Validate file type
        const allowedTypes = [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "application/pdf",
        ];
        if (!allowedTypes.includes(data.paProofOfIdentity.type)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Proof of identity must be a JPEG, JPG, PNG, or PDF file",
            path: ["paProofOfIdentity"],
          });
        }

        // Validate file size (max 5 MB)
        const maxSizeInBytes = 5 * 1024 * 1024; // 5 MB
        if (data.paProofOfIdentity.size > maxSizeInBytes) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Proof of identity file size must not exceed 5 MB",
            path: ["paProofOfIdentity"],
          });
        }
      }
    }
  });

/* ==================================================================
 * Atomic blocks
 * ================================================================== */

const abstractMetaSchema = z.object({
  abstract_mode_id: z.string().min(1, "Presentation mode is required"),
  abstract_category_id: z.string().min(1, "Abstract category is required"),
  abstract_topic_id: z.string().min(1, "Abstract topic is required"),
});

const institutionsSchema = z
  .array(institutionSchema)
  .min(1, "At least one institution is required");

const authorsSchema = (maxCorrespondingAuthors: number) =>
  z
    .array(authorSchema)
    .min(1, "At least one author is required")
    .refine(
      (authors) => {
        const correspondingAuthors = authors.filter((a) => a.is_corresponding);
        return (
          correspondingAuthors.length >= 1 &&
          correspondingAuthors.length <= maxCorrespondingAuthors
        );
      },
      {
        message: `There must be between 1 and ${maxCorrespondingAuthors} corresponding authors`,
      },
    )
    .refine(
      (authors) => {
        // All authors must have positions assigned
        const allAuthorsHavePositions = authors.every(
          (author) => author.position && author.position.trim() !== "",
        );

        if (!allAuthorsHavePositions) {
          return false;
        }

        // For multiple authors, validate position uniqueness and main author
        if (authors.length > 1) {
          const positions = authors.map((a) => a.position).filter((p) => p);
          const mainAuthorCount = positions.filter((p) => p === "1").length;
          const hasDuplicates = new Set(positions).size !== positions.length;
          return mainAuthorCount === 1 && !hasDuplicates;
        }

        return true;
      },
      {
        message: "Assign unique positions (main/co-authors) to all authors.",
      },
    );

/* ==================================================================
 * Step 3 – HTML content
 * ================================================================== */

const htmlContentSchema = z.object({
  title: z
    .string()
    .min(1, "Title is required")
    .refine(
      (title) => {
        // Strip HTML tags
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = title;
        const textContent = tempDiv.textContent || tempDiv.innerText || "";
        return textContent.length <= 500;
      },
      { message: "Title must not exceed 500 characters" },
    ),
  content: z
    .string()
    .min(1, "Content is required")
    .refine(
      (content) => {
        // Strip HTML tags the same way as the UI counter
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = content;
        const textContent = tempDiv.textContent || tempDiv.innerText || "";
        const wordCount = textContent
          .trim()
          .split(/\s+/)
          .filter((word) => word.length > 0).length;
        return wordCount >= 1 && wordCount <= 310;
      },
      { message: "Content must be between 1 and 310 words" },
    ),
  abstractImages: z
    .array(
      z.object({
        file: z.instanceof(File),
        preview: z.string(),
        title: z.string().optional(),
        position: z.number(),
      }),
    )
    .max(2, "Maximum 2 images allowed"),
  imagePosition: z.enum(["side_to_side", "up_and_down"], {
    message: "Please select an image display format",
  }),
  keywords: z
    .array(z.string())
    .min(1, "At least one keyword is required")
    .max(5, "Maximum 5 keywords allowed"),
  references: z
    .array(z.string())
    .max(5, "Maximum 5 references allowed")
    .refine(
      (references) => {
        return references.every((ref) => {
          // Skip empty references
          if (!ref || ref.trim() === "") return true;

          const wordCount = ref
            .trim()
            .split(/\s+/)
            .filter((word) => word.length > 0).length;

          return wordCount >= 1 && wordCount <= 50;
        });
      },
      { message: "Each reference must be between 1 and 50 words" },
    ),
});

/* ==================================================================
 * File upload step
 * ================================================================== */

const fileUploadSchema = z.object({
  title: z
    .string()
    .min(1, "Title is required")
    .max(500, "Title must not exceed 500 characters"),
  abstractFile: z
    .instanceof(File, { message: "Abstract file is required" })
    .refine((file) => file.type === DOCX_MIME_TYPE, {
      message: "Abstract file must be a DOCX file",
    })
    .refine(
      (file) => file.size <= 10 * 1024 * 1024, // 10MB
      { message: "Abstract file must be less than 10MB" },
    ),
});

/* ==================================================================
 * STEP SCHEMA FACTORIES (NO PRE-INSTANTIATION)
 * ================================================================== */

export const createStep1Schema = () => abstractMetaSchema;

export const createStep2Schema = (maxCorrespondingAuthors: number) =>
  z.object({
    institutions: institutionsSchema,
    authors: authorsSchema(maxCorrespondingAuthors),
  });

export const createStep3Schema = () => htmlContentSchema;

export const createFileStepSchema = () => fileUploadSchema;

/* ==================================================================
 * MODE SCHEMA FACTORY (CALLED FROM PROPS)
 * ================================================================== */

export const createSchemasByMode = (
  mode: "html" | "file",
  maxCorrespondingAuthors: number,
) => {
  const step1 = createStep1Schema();
  const step2 = createStep2Schema(maxCorrespondingAuthors);

  if (mode === "html") {
    return {
      step1,
      step2,
      step3: createStep3Schema(),
    };
  }

  return {
    step1,
    step2,
    step3: createFileStepSchema(),
  };
};

/* ==================================================================
 * Validation helper
 * ================================================================== */

export const validateStep = (data: any, schema: z.ZodSchema) => {
  const result = schema.safeParse(data);

  if (result.success) {
    return { isValid: true, data: result.data };
  }

  const errors: Record<string, string> = {};
  result.error.issues.forEach((issue) => {
    errors[issue.path.join(".")] = issue.message;
  });

  return { isValid: false, errors };
};
